import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, Phone, MapPin } from "lucide-react";
import { createLead } from "@/lib/api";
import { BRAND, SERVICES } from "@/data/content";

const BUDGETS = ["< $2k", "$2k–$5k", "$5k–$10k", "$10k+"];

export const ContactSection = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    service: SERVICES[0].title, budget: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await createLead(form);
      setDone(true);
      toast.success("Thanks! We'll be in touch within 1 business day.");
      setForm({ name: "", email: "", phone: "", company: "", service: SERVICES[0].title, budget: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-line py-3 text-white placeholder:text-text-2/60 focus:outline-none focus:border-accent transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 border-t border-line">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="mono-label text-accent">[ Start a project ]</span>
            <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 text-balance">
              Let's grow your business.
            </h2>
            <p className="text-text-2 text-lg leading-relaxed mt-6 max-w-md">
              Tell us about your goals and we'll send a tailored plan — no pressure, no jargon.
              Free consultation for Sacramento businesses.
            </p>

            <div className="mt-10 space-y-5">
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-4 text-white/90 hover:text-accent transition-colors" data-testid="contact-email">
                <span className="w-11 h-11 flex items-center justify-center border border-line"><Mail className="w-5 h-5" /></span>
                {BRAND.email}
              </a>
              <a href={`tel:${BRAND.phoneTel}`} className="flex items-center gap-4 text-white/90 hover:text-accent transition-colors" data-testid="contact-phone">
                <span className="w-11 h-11 flex items-center justify-center border border-line"><Phone className="w-5 h-5" /></span>
                {BRAND.phone}
              </a>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=3525+4th+Ave,+Sacramento,+CA+95817"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-address"
                className="flex items-center gap-4 text-white/90 hover:text-accent transition-colors"
              >
                <span className="w-11 h-11 flex items-center justify-center border border-line"><MapPin className="w-5 h-5" /></span>
                {BRAND.address}
              </a>
            </div>

            <a
              href="https://www.google.com/maps/dir/?api=1&destination=3525+4th+Ave,+Sacramento,+CA+95817"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-map"
              className="block mt-8 border border-line overflow-hidden group"
            >
              <iframe
                title="Emergent Sacramento office map"
                src="https://www.google.com/maps?q=3525+4th+Ave,+Sacramento,+CA+95817&output=embed"
                className="w-full h-64 grayscale contrast-125 group-hover:grayscale-0 transition-all duration-500 pointer-events-none"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-surface border border-line p-8 lg:p-10"
          >
            {done ? (
              <div data-testid="contact-success" className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-16 h-16 rounded-full bg-accent/15 border border-accent flex items-center justify-center mb-6">
                  <ArrowUpRight className="w-7 h-7 text-accent" />
                </div>
                <h3 className="font-head font-bold text-2xl">Message received.</h3>
                <p className="text-text-2 mt-2 max-w-xs">We'll reach out within one business day. Talk soon!</p>
                <button onClick={() => setDone(false)} className="mt-8 mono-label text-accent" data-testid="contact-send-another">
                  Send another →
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <input data-testid="lead-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                  <input data-testid="lead-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
                  <input data-testid="lead-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
                  <input data-testid="lead-company" className={inputCls} placeholder="Company" value={form.company} onChange={set("company")} />
                </div>

                <div>
                  <label className="mono-label text-text-2 block mb-3">Service needed</label>
                  <select data-testid="lead-service" className={`${inputCls} appearance-none cursor-pointer`} value={form.service} onChange={set("service")}>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title} className="bg-surface text-white">{s.title}</option>
                    ))}
                    <option value="Not sure yet" className="bg-surface text-white">Not sure yet</option>
                  </select>
                </div>

                <div>
                  <label className="mono-label text-text-2 block mb-3">Budget</label>
                  <div className="flex flex-wrap gap-2">
                    {BUDGETS.map((b) => (
                      <button
                        type="button"
                        key={b}
                        data-testid={`budget-${b}`}
                        onClick={() => setForm({ ...form, budget: b })}
                        className={`px-4 py-2 text-sm border transition-colors ${
                          form.budget === b ? "border-accent text-accent bg-accent/10" : "border-line text-text-2 hover:border-white/30"
                        }`}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                </div>

                <textarea data-testid="lead-message" rows={4} className={inputCls + " resize-none"} placeholder="Tell us about your project *" value={form.message} onChange={set("message")} />

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-accent text-ink font-head font-bold py-4 hover:bg-white transition-colors disabled:opacity-60"
                >
                  {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : <>Send message <ArrowUpRight className="w-5 h-5" /></>}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
